/**
 * The streaming transport for provider calls (18.08.2026).
 *
 * `providerHttp.js` explains why provider calls go through Rust rather than
 * fetch: under Tauri the webview's fetch is subject to CORS and provider
 * endpoints do not all allow browser origins. That reasoning holds here — this
 * is the same native client, opened as a stream instead of buffered.
 *
 * The division of labour is deliberate and worth keeping: **Rust frames, JS
 * parses.** Rust splits the byte stream into `data:` payloads and forwards them
 * verbatim; it does not know that `[DONE]` is OpenAI's sentinel or that
 * `content_block_delta` is Anthropic's. Teaching it would put three providers'
 * protocols in a language that cannot see the adapters, and every new provider
 * would then need a Rust change.
 *
 * Availability: streaming needs the Tauri command. In the browser build there
 * is no `invoke`, so `canStream()` is false and callers keep the buffered path.
 */
import { isTauri } from '../../utils/tauri';

/**
 * The Tauri APIs are imported lazily, and that is load-bearing rather than
 * tidiness. `@tauri-apps/api/*` ships untranspiled ESM, so a static import here
 * is inherited by everything that reaches this module — the Anthropic adapter,
 * the engine, and every test that touches either. It also drags the native
 * bridge into the browser build, which has no use for it.
 *
 * Nothing above `streamProviderSse` needs them: `canStream()` answers from
 * `isTauri()` alone.
 */
async function tauriApis() {
  const [core, event] = await Promise.all([
    import('@tauri-apps/api/core'),
    import('@tauri-apps/api/event'),
  ]);
  return { invoke: core.invoke, listen: event.listen };
}

/** Whether a streamed turn is possible at all in this runtime. */
export function canStream() {
  return isTauri();
}

let streamCounter = 0;

/** Ids only have to be unique within this process, and readable in a log. */
function nextStreamId() {
  streamCounter += 1;
  return `ai-${Date.now().toString(36)}-${streamCounter}`;
}

/**
 * Open a streamed provider call.
 *
 * `onPayload` receives each `data:` payload as a raw string, in order — the
 * caller's adapter decides what it means. Resolves when the stream ends;
 * rejects if it never opened, or if it fails mid-stream.
 *
 * Returns via `options.onStart` a `cancel()` that aborts the Rust task rather
 * than merely unsubscribing. That distinction is the point: on the user's own
 * API key, a "stopped" turn that keeps generating is a turn they still pay for.
 */
export async function streamProviderSse(url, headers, bodyObj, onPayload, { onStart } = {}) {
  const { invoke, listen } = await tauriApis();
  const streamId = nextStreamId();
  let unlistenAll = [];
  let settled = false;

  const cleanup = () => {
    for (const un of unlistenAll) {
      try { un(); } catch { /* already gone */ }
    }
    unlistenAll = [];
  };

  return new Promise((resolve, reject) => {
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    Promise.all([
      listen(`ai-stream-chunk-${streamId}`, (event) => {
        // A throwing adapter must not strand the stream: the Rust task would
        // keep running and the promise would never settle.
        try {
          onPayload(event.payload);
        } catch (e) {
          invoke('ai_stream_cancel', { streamId }).catch(() => {});
          finish(reject, e instanceof Error ? e : new Error(String(e)));
        }
      }),
      listen(`ai-stream-done-${streamId}`, () => finish(resolve, undefined)),
      listen(`ai-stream-error-${streamId}`, (event) => {
        finish(reject, new Error(String(event.payload || 'Stream failed')));
      }),
    ])
      .then((unlisteners) => {
        unlistenAll = unlisteners;
        // Registered only once listeners exist — a chunk emitted before them
        // would be dropped, and the first chunk is where the error usually is.
        onStart?.({
          streamId,
          cancel: () => {
            // Abort first, settle second: cancelling is not an error, and a
            // cancelled turn should resolve with whatever already arrived.
            invoke('ai_stream_cancel', { streamId }).catch(() => {});
            finish(resolve, undefined);
          },
        });
        return invoke('ai_stream_start', {
          streamId,
          url,
          headers: { 'content-type': 'application/json', ...headers },
          body: bodyObj,
        });
      })
      .catch((e) => finish(reject, e instanceof Error ? e : new Error(String(e))));
  });
}

// The parsers live in sseParsers.js — pure, and free of the Tauri import above.
export {
  parseAnthropicPayload,
  parseOpenAiPayload,
  parseGeminiPayload,
  STREAM_PARSERS,
} from './sseParsers';
